// SPDX-License-Identifier: Apache-2.0

// VaultModal — markdown viewer for the selected doc.
//
// Solid port of the old React `vault-modal.tsx`. The body uses the framework-
// free `<tether-doc>` custom element from `@tether/viewer`: importing the
// package once at the top side-effect-registers the element on the global
// custom-element registry; we just drop `<tether-doc>` in the JSX with the
// doc's content as the slot text. Wikilinks, math, mermaid, and Shiki-style
// code highlighting all happen inside the element's shadow DOM with zero
// extra wiring on this side.
//
// Frontmatter is still shown as a small badge row at the top — the chip row
// inside `<tether-doc>` handles its own frontmatter render, but we keep the
// outer row too because the modal-level chips are tinted by the vault's
// per-doc tint, which the framework-free viewer doesn't know about.
//
// Edit mode is a textarea over the same content; "save" writes back through
// the vault hook (which re-parses frontmatter via the storage layer).

import { createSignal, createMemo, createEffect, onCleanup, onMount, Show, For } from "solid-js";
import type { VaultDoc } from "@tether/storage";
import type { UseVault } from "./useVault";

// Side-effect import: registers <tether-doc> as a custom element. After this
// runs once anywhere in the app, the tag is available everywhere.
import { TetherDoc as TetherDocElement } from "@tether/viewer";

import { WikilinkAutocomplete } from "./WikilinkAutocomplete";
import { TagAutocomplete } from "./TagAutocomplete";
import { EditHistoryViewer } from "./EditHistoryViewer";
import { useVaultGraph } from "./use-vault-graph";
import "./autocomplete.css";

// Solid uses TypeScript's JSX namespace; teach it about <tether-doc>.
declare module "solid-js" {
  namespace JSX {
    interface IntrinsicElements {
      "tether-doc": JSX.HTMLAttributes<TetherDocElement> & {
        src?: string;
        theme?: "dark" | "light";
        base?: string;
        "light-dom"?: "true" | "false";
      };
    }
  }
}

const TINT_HEX: Record<VaultDoc["tint"], string> = {
  cyan: "#67e8f9",
  violet: "#a78bfa",
  amber: "#fbbf24",
  rose: "#ff7a94",
};

interface VaultModalProps {
  doc: VaultDoc;
  vault: UseVault;
  onClose: () => void;
  /** Per-tint label override (DavaKasası uses these for privilege classes). */
  tintLabels?: Partial<Record<VaultDoc["tint"], string>>;
}

export function VaultModal(props: VaultModalProps) {
  const [editing, setEditing] = createSignal(false);
  const [draftTitle, setDraftTitle] = createSignal(props.doc.title);
  const [draftContent, setDraftContent] = createSignal(props.doc.content);
  const [confirmDelete, setConfirmDelete] = createSignal(false);
  const [historyOpen, setHistoryOpen] = createSignal(false);

  // Ref to the editor textarea — exposed to the autocomplete popovers so they
  // can read selection state and listen for keystrokes. We use a Solid signal
  // so the popover effects re-run when the textarea mounts/unmounts.
  const [textareaEl, setTextareaEl] = createSignal<HTMLTextAreaElement | undefined>(undefined);

  // Reset the drafts whenever the active doc changes. createEffect tracks
  // props.doc.id (reactive props are getters in Solid).
  let lastSeenId = props.doc.id;
  createEffect(() => {
    const id = props.doc.id;
    if (id !== lastSeenId) {
      lastSeenId = id;
      setEditing(false);
      setDraftTitle(props.doc.title);
      setDraftContent(props.doc.content);
      setConfirmDelete(false);
      setHistoryOpen(false);
    }
  });

  const tint = createMemo(() => TINT_HEX[props.doc.tint] ?? "#a78bfa");
  const tintLabel = createMemo(() => props.tintLabels?.[props.doc.tint]);

  // Related-docs panel — k-hop neighbours of the current doc. The hook is
  // memoised on docs(); reads stay cheap even when the user is clicking
  // through the orbit fast. We keep two rings (1-hop bright, 2-hop faded)
  // so the user gets the same "context map" v1 had.
  const graph = useVaultGraph(() => props.vault.docs());

  /** Top N 1-hop neighbours, by recency (createdAt desc). */
  const oneHop = createMemo<VaultDoc[]>(() => {
    const fn = graph.kHopDocs();
    const docs = fn(props.doc.id, 1);
    docs.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    return docs.slice(0, 8);
  });

  /** 2-hop neighbours minus 1-hop set — pure 2nd ring, capped at 8. */
  const twoHop = createMemo<VaultDoc[]>(() => {
    const fn = graph.kHopDocs();
    const all = fn(props.doc.id, 2);
    const oneSet = new Set(oneHop().map((d) => d.id));
    const out = all.filter((d) => !oneSet.has(d.id));
    out.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
    return out.slice(0, 8);
  });

  /** Hide the panel entirely if the doc has no neighbours either way. */
  const hasRelated = createMemo(
    () => oneHop().length > 0 || twoHop().length > 0,
  );

  function selectRelated(id: string) {
    props.vault.setActiveId(id);
  }

  const frontEntries = createMemo(() => {
    const f = props.doc.front ?? {};
    const out: Array<{ k: string; v: string }> = [];
    for (const [k, v] of Object.entries(f)) {
      if (v == null || v === "") continue;
      out.push({ k, v: Array.isArray(v) ? v.join(", ") : String(v) });
    }
    return out;
  });

  const dirty = createMemo(
    () =>
      editing() &&
      (draftTitle() !== props.doc.title || draftContent() !== props.doc.content),
  );

  function commit() {
    const patch: { title?: string; content?: string } = {};
    if (draftTitle() !== props.doc.title) patch.title = draftTitle();
    if (draftContent() !== props.doc.content) patch.content = draftContent();
    if (patch.title || patch.content) {
      void props.vault.update(props.doc.id, patch);
    }
    setEditing(false);
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      props.onClose();
    }
  }
  onMount(() => window.addEventListener("keydown", onKey));
  onCleanup(() => window.removeEventListener("keydown", onKey));

  function handleDelete() {
    if (confirmDelete()) {
      void props.vault.remove(props.doc.id);
      props.onClose();
      return;
    }
    setConfirmDelete(true);
    setTimeout(() => setConfirmDelete(false), 2500);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      class="vault-modal__backdrop"
      onClick={props.onClose}
      data-testid="vault-modal"
    >
      <div
        class="vault-modal__panel"
        onClick={(e) => e.stopPropagation()}
        style={{
          "border-color": `${tint()}33`,
          "box-shadow": `0 0 60px ${tint()}22, 0 24px 80px rgba(0, 0, 0, 0.55)`,
        }}
      >
        {/* Header */}
        <div class="vault-modal__header">
          <span
            class="vault-modal__tint-dot"
            style={{
              background: tint(),
              "box-shadow": `0 0 10px ${tint()}`,
            }}
            title={tintLabel()}
          />
          <Show when={tintLabel()}>
            <span
              class="vault-modal__tint-label"
              style={{
                color: tint(),
                background: `${tint()}14`,
                "border-color": `${tint()}3a`,
              }}
            >
              {tintLabel()}
            </span>
          </Show>

          <Show
            when={editing()}
            fallback={<span class="vault-modal__title">{props.doc.title}</span>}
          >
            <input
              class="vault-modal__title-input"
              value={draftTitle()}
              onInput={(e) => setDraftTitle(e.currentTarget.value)}
            />
          </Show>

          <button
            class="vault-modal__icon-btn"
            onClick={() => (editing() ? commit() : setEditing(true))}
            title={editing() ? "save" : "edit"}
          >
            {editing() ? (dirty() ? "✓" : "·") : "✎"}
          </button>
          <button
            class="vault-modal__icon-btn"
            classList={{ "is-danger": historyOpen() }}
            onClick={() => setHistoryOpen((v) => !v)}
            title={historyOpen() ? "close history" : "edit history"}
            data-testid="history-toggle"
          >
            ⟲
          </button>
          <button
            class="vault-modal__icon-btn"
            classList={{ "is-danger": confirmDelete() }}
            onClick={handleDelete}
            title={confirmDelete() ? "press again to delete" : "delete"}
          >
            🗑
          </button>
          <button
            class="vault-modal__icon-btn"
            onClick={props.onClose}
            title="close (esc)"
          >
            ✕
          </button>
        </div>

        {/* Frontmatter badges */}
        <Show when={!editing() && frontEntries().length > 0}>
          <div class="vault-modal__frontmatter">
            <For each={frontEntries()}>
              {(entry) => (
                <span class="vault-modal__front-chip">
                  <span class="vault-modal__front-key">{entry.k}</span>
                  <span class="vault-modal__front-val">{entry.v}</span>
                </span>
              )}
            </For>
          </div>
        </Show>

        {/* Body */}
        <div class="vault-modal__body">
          <Show
            when={historyOpen()}
            fallback={
              <Show
                when={editing()}
                fallback={
                  <tether-doc
                    ref={(el: TetherDocElement) => {
                      // Imperative push on every doc change. Custom-element
                      // observed-attributes don't react to body text, so we
                      // drive it via setContent(). createEffect tracks
                      // props.doc.content.
                      //
                      // Guard against the ref running before the custom
                      // element has been upgraded (HMR reload, ordering
                      // races) — `setContent` is a class method, missing on
                      // the upgrade-pending HTMLElement placeholder.
                      createEffect(() => {
                        if (typeof el.setContent === "function") {
                          void el.setContent(props.doc.content);
                        }
                      });
                    }}
                    class="vault-modal__rendered"
                    theme="dark"
                    base="/n"
                  />
                }
              >
                <>
                  <textarea
                    class="vault-modal__editor"
                    value={draftContent()}
                    spellcheck={false}
                    onInput={(e) => setDraftContent(e.currentTarget.value)}
                    ref={(el) => setTextareaEl(el)}
                    data-testid="vault-modal-editor"
                  />
                  <WikilinkAutocomplete
                    textareaRef={textareaEl}
                    value={draftContent}
                    setValue={setDraftContent}
                    vault={props.vault}
                  />
                  <TagAutocomplete
                    textareaRef={textareaEl}
                    value={draftContent}
                    setValue={setDraftContent}
                    vault={props.vault}
                  />
                </>
              </Show>
            }
          >
            <EditHistoryViewer
              doc={props.doc}
              onRestored={() => {
                // After restore, the live Y.Doc has been mutated. Pull the
                // text back through the store so the modal's draft tracks it.
                void props.vault.store.syncFromYjs(props.doc.id).then(() => {
                  const fresh = props.vault.store.getDoc(props.doc.id);
                  if (fresh) setDraftContent(fresh.content);
                });
              }}
            />
          </Show>
        </div>

        {/* Related-docs panel — 1-hop bright, 2-hop faded. Hidden when the
            doc has no neighbours in either direction; also hidden in edit
            and history modes so the chrome stays focused on the active
            interaction. */}
        <Show when={!editing() && !historyOpen() && hasRelated()}>
          <div class="vault-modal__related" data-testid="vault-modal-related">
            <Show when={oneHop().length > 0}>
              <div class="vault-modal__related-row">
                <span class="vault-modal__related-label">related</span>
                <For each={oneHop()}>
                  {(d) => (
                    <button
                      type="button"
                      class="vault-modal__related-chip"
                      data-testid="vault-modal-related-1hop"
                      onClick={() => selectRelated(d.id)}
                      title={d.title}
                    >
                      <span
                        class="vault-modal__related-dot"
                        style={{
                          background: TINT_HEX[d.tint] ?? "#a78bfa",
                          "box-shadow": `0 0 6px ${TINT_HEX[d.tint] ?? "#a78bfa"}`,
                        }}
                      />
                      <span class="vault-modal__related-title">
                        {d.title.replace(/\.md$/i, "")}
                      </span>
                    </button>
                  )}
                </For>
              </div>
            </Show>
            <Show when={twoHop().length > 0}>
              <div class="vault-modal__related-row vault-modal__related-row--faded">
                <span class="vault-modal__related-label">2 hops</span>
                <For each={twoHop()}>
                  {(d) => (
                    <button
                      type="button"
                      class="vault-modal__related-chip vault-modal__related-chip--faded"
                      data-testid="vault-modal-related-2hop"
                      onClick={() => selectRelated(d.id)}
                      title={d.title}
                    >
                      <span
                        class="vault-modal__related-dot"
                        style={{
                          background: TINT_HEX[d.tint] ?? "#a78bfa",
                        }}
                      />
                      <span class="vault-modal__related-title">
                        {d.title.replace(/\.md$/i, "")}
                      </span>
                    </button>
                  )}
                </For>
              </div>
            </Show>
          </div>
        </Show>
      </div>
    </div>
  );
}

// Markdown rendering now lives in `<tether-doc>` (@tether/viewer). The old
// hand-rolled inline renderer was deleted in this commit — see git history if
// you ever need to compare its output to the framework-free viewer.
